import { API_ENDPOINTS, getApiUrl } from '../config/api';

/** An advertised upcoming event (not ticketed on Zoniq yet). Posters are admin uploads. */
export interface Announcement {
  id: number;
  name: string;
  /** YYYY-MM-DD */
  eventDate: string;
  city: string;
  /** ~800px WebP for cards */
  posterUrl: string;
  /** ~1600px WebP for the full-size viewer */
  posterLargeUrl: string;
}

export interface AdminAnnouncement extends Announcement {
  posterOriginalUrl: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('zoniq_token');
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}` };
};

async function parse<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    let msg = err.detail ?? `${fallback} (${response.status})`;
    if (Array.isArray(msg)) msg = msg[0]?.msg || fallback;
    throw new Error(String(msg));
  }
  return response.json();
}

export const announcementsApi = {
  /** Public: active announcements with upcoming dates, soonest first. */
  list: async (): Promise<Announcement[]> =>
    parse(await fetch(getApiUrl(API_ENDPOINTS.ANNOUNCEMENTS)), 'Failed to load upcoming events'),

  adminList: async (): Promise<AdminAnnouncement[]> => {
    const data = await parse<{ items: AdminAnnouncement[] }>(
      await fetch(getApiUrl(API_ENDPOINTS.ADMIN.ANNOUNCEMENTS.BASE), { headers: authHeader() }),
      'Failed to load announcements'
    );
    return data.items;
  },

  create: async (input: { name: string; eventDate: string; city: string; poster: File }): Promise<AdminAnnouncement> => {
    const form = new FormData();
    form.append('name', input.name);
    form.append('event_date', input.eventDate);
    form.append('city', input.city);
    form.append('poster', input.poster);
    return parse(
      await fetch(getApiUrl(API_ENDPOINTS.ADMIN.ANNOUNCEMENTS.BASE), { method: 'POST', headers: authHeader(), body: form }),
      'Failed to add upcoming event'
    );
  },

  update: async (
    id: number,
    input: { name?: string; eventDate?: string; city?: string; isActive?: boolean; poster?: File | null }
  ): Promise<AdminAnnouncement> => {
    const form = new FormData();
    if (input.name !== undefined) form.append('name', input.name);
    if (input.eventDate !== undefined) form.append('event_date', input.eventDate);
    if (input.city !== undefined) form.append('city', input.city);
    if (input.isActive !== undefined) form.append('is_active', String(input.isActive));
    if (input.poster) form.append('poster', input.poster);
    return parse(
      await fetch(getApiUrl(API_ENDPOINTS.ADMIN.ANNOUNCEMENTS.DETAIL(id)), { method: 'PATCH', headers: authHeader(), body: form }),
      'Failed to update upcoming event'
    );
  },

  remove: async (id: number): Promise<void> => {
    await parse(
      await fetch(getApiUrl(API_ENDPOINTS.ADMIN.ANNOUNCEMENTS.DETAIL(id)), { method: 'DELETE', headers: authHeader() }),
      'Failed to delete upcoming event'
    );
  },
};
